import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { AuthContext } from '../../components/AuthContext';
import Select from 'react-select';
import { ClockIcon } from '@heroicons/react/24/outline';
import ProgressModal from '../../components/ProgressModal';
import UpdatedByModal from '../../components/UpdatedByModal';
import useDraftManager from '../../hooks/useDraftManager';
import { toast } from 'react-toastify';

const ItemManagement = () => {
  const [brands, setBrands] = useState([]);
  const [items, setItems] = useState([]);
  const { user } = useContext(AuthContext);
  
  // Dark mode state
  const [darkMode, setDarkMode] = useState(() => {
    const savedMode = localStorage.getItem('darkMode');
    return savedMode === 'true';
  });

  // Enhanced dark mode sync with localStorage and system preference
  useEffect(() => {
    const updateDarkMode = () => {
      const savedMode = localStorage.getItem('darkMode');
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const isDark = savedMode === 'true' || (savedMode === null && systemPrefersDark);
      setDarkMode(isDark);
    };

    // Initial check
    updateDarkMode();

    // Listen for localStorage changes
    const interval = setInterval(updateDarkMode, 500);

    // Listen for system theme changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    mediaQuery.addEventListener('change', updateDarkMode);

    return () => {
      clearInterval(interval);
      mediaQuery.removeEventListener('change', updateDarkMode);
    };
  }, []);
  
  const [form, setForm] = useState({
    brand: '',
    category: '',
    skuCode: '',
    skuDescription: '',
    series: 'RS',
    hsnCode: '',
    gstRate: '',
    mrp: '',
    offerType: {
      nrp: '',
      clp: '',
      sdp: '',
      kap: '',
      hvr: '',
      spl: '',
    },
    image: null,
    additionalFieldValues: {}
  });
  const [isEditing, setIsEditing] = useState(false);
  const [filteredItems, setFilteredItems] = useState(items);
  const [filter, setFilter] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  const [totalItems, setTotalItems] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(5);

  // Modal state for showing details
  const [modalOpen, setModalOpen] = useState(false);
  const [modalItem, setModalItem] = useState(null);
  const [expandedUpdates, setExpandedUpdates] = useState({});

  const [loading, setLoading] = useState(true);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [brandAdditionalFields, setBrandAdditionalFields] = useState([]);

  // Progress modal state
  const [progressModal, setProgressModal] = useState({
    isOpen: false,
    title: '',
    steps: [],
    currentStep: 0,
    progress: 0,
    message: ''
  });

  // UpdatedBy modal state
  const [updatedByModal, setUpdatedByModal] = useState({
    isOpen: false,
    item: null
  });

  // Toast notification state
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');

  // Convert File to Data URL for draft storage
  const convertImageToDataUrl = useCallback(async (file) => {
    return new Promise((resolve, reject) => {
      if (!file || typeof file === 'string') {
        resolve(file); // Already a string or null
        return;
      }
      
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }, []);

  // Convert Data URL back to File object (for form submission)
  const convertDataUrlToFile = useCallback(async (dataUrl, filename, fileSize) => {
    if (!dataUrl || typeof dataUrl !== 'string' || !dataUrl.startsWith('data:')) {
      return dataUrl; // Not a data URL
    }

    try {
      const response = await fetch(dataUrl);
      const blob = await response.blob();
      
      // Create a File object from the blob
      const file = new File([blob], filename || 'image.jpg', {
        type: blob.type,
        lastModified: Date.now()
      });
      
      console.log('🔄 Converted data URL back to File:', file.name, file.size);
      return file;
    } catch (error) {
      console.error('Error converting data URL to File:', error);
      return dataUrl; // Return original if conversion fails
    }
  }, []);

  // Prepare form data for draft saving (convert images)
  const prepareFormDataForDraft = useCallback(async (formData) => {
    try {
      const draftData = { ...formData };
      
      // Convert image File to data URL for storage
      if (draftData.image instanceof File) {
        console.log('🖼️ Converting image File to data URL for draft');
        draftData.image = await convertImageToDataUrl(draftData.image);
        draftData._imageFileName = formData.image.name; // Store original filename
        draftData._imageFileSize = formData.image.size; // Store file size
      }
      
      return draftData;
    } catch (error) {
      console.error('Error preparing form data for draft:', error);
      return formData;
    }
  }, [convertImageToDataUrl]);

  // Initialize draft manager with image handling
  const draftManager = useDraftManager({
    userId: user?.user?._id,
    companyId: user?.user?.company?._id || user?.user?.company,
    featureType: 'itemManagement',
    formData: form,
    setFormData: setForm,
    prepareFormData: prepareFormDataForDraft, // Custom data preparation
    onError: (error) => {
      console.error('Draft error:', error);
    },
    onDraftSaved: (savedDraft, isManual) => {
      const hasImage = savedDraft.draftData?.image && (
        savedDraft.draftData.image instanceof File || 
        typeof savedDraft.draftData.image === 'string'
      );
      const imageInfo = hasImage ? ' (including image)' : '';
      const saveType = isManual ? 'manually' : 'automatically';
      
      setNotificationMessage(`Draft saved ${saveType}${imageInfo}`);
      setShowNotification(true);
      
      // Update image preview if draft contains image data URL
      if (hasImage && typeof savedDraft.draftData.image === 'string') {
        console.log('🖼️ Updating image preview from saved draft data URL');
        setImagePreview(savedDraft.draftData.image);
      }
    },
    onDraftLoaded: (loadedDraft) => {
      console.log('🔷 Draft loaded, waiting for brands...', loadedDraft);
      
      // Check if draft contains image data
      if (loadedDraft.draftData?.image && typeof loadedDraft.draftData.image === 'string') {
        console.log('🖼️ Draft contains image data:', loadedDraft.draftData._imageFileName || 'Unknown filename');
        
        // Show notification about image loading
        setTimeout(() => {
          setNotificationMessage(`Draft loaded with saved image: ${loadedDraft.draftData._imageFileName || 'image file'}`);
          setShowNotification(true);
        }, 1000); // Delay to show after initial loading
      }
      
      // Don't handle here, let the effect below handle it after brands are loaded
    }
  });

  // Handle draft loading after brands are available
  useEffect(() => {
    if (!brands || brands.length === 0) {
      console.log('⏳ Waiting for brands to load...');
      return;
    }

    // Only process if we have draft data in the form
    if (!form.brand && !form.category) {
      return;
    }

    console.log('🔷 Processing draft with brands available', { formBrand: form.brand, formCategory: form.category });

    // Handle brand selection from draft
    if (form.brand) {
      const brandOption = brands.find(b => b.value === form.brand);
      console.log('🔷 Found brand option:', brandOption);
      if (brandOption) {
        setSelectedBrand(brandOption);
        setBrandAdditionalFields(brandOption.additionalFields || []);

        // Handle category selection from draft
        if (form.category && brandOption.categories) {
          const categoryData = brandOption.categories.find(c => c._id === form.category);
          console.log('🔷 Found category data:', categoryData);
          if (categoryData) {
            const categoryOption = { value: categoryData._id, label: categoryData.name };
            setSelectedCategory(categoryOption);
          }
        }
      }
    }

    // Handle image preview from draft
    if (form.image && typeof form.image === 'string') {
      console.log('🖼️ Setting image preview from draft data URL');
      setImagePreview(form.image);
    }
  }, [brands, form.brand, form.category, form.image]); // Run when brands load or form data changes

  // Auto-hide notification after 3 seconds
  useEffect(() => {
    if (showNotification) {
      const timer = setTimeout(() => {
        setShowNotification(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showNotification]);

  const toggleUpdateDetails = (updateId) => {
    setExpandedUpdates(prev => ({
      ...prev,
      [updateId]: !prev[updateId]
    }));
  };

  // Enhanced CSS for animations, scrollbars, and dark mode effects
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes modalSlideIn {
        from {
          opacity: 0;
          transform: scale(0.95) translateY(-10px);
        }
        to {
          opacity: 1;
          transform: scale(1) translateY(0);
        }
      }
      
      @keyframes slideInFromRight {
        from {
          opacity: 0;
          transform: translateX(100%);
        }
        to {
          opacity: 1;
          transform: translateX(0);
        }
      }
      
      @keyframes fadeIn {
        from {
          opacity: 0;
        }
        to {
          opacity: 1;
        }
      }
      
      .animate-pulse-scale {
        animation: modalSlideIn 0.4s cubic-bezier(0.4, 0, 0.2, 1);
      }
      
      .animate-fade-in {
        animation: fadeIn 0.3s ease-out;
      }
      
      /* Enhanced scrollbar styles */
      .scrollbar-light::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }
      .scrollbar-light::-webkit-scrollbar-track {
        background: #f8fafc;
        border-radius: 4px;
      }
      .scrollbar-light::-webkit-scrollbar-thumb {
        background: #cbd5e1;
        border-radius: 4px;
        border: 2px solid #f8fafc;
      }
      .scrollbar-light::-webkit-scrollbar-thumb:hover {
        background: #94a3b8;
      }
      .scrollbar-light::-webkit-scrollbar-corner {
        background: #f8fafc;
      }
      
      .scrollbar-dark::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }
      .scrollbar-dark::-webkit-scrollbar-track {
        background: #1f2937;
        border-radius: 4px;
      }
      .scrollbar-dark::-webkit-scrollbar-thumb {
        background: #4b5563;
        border-radius: 4px;
        border: 2px solid #1f2937;
      }
      .scrollbar-dark::-webkit-scrollbar-thumb:hover {
        background: #6b7280;
      }
      .scrollbar-dark::-webkit-scrollbar-corner {
        background: #1f2937;
      }
      
      /* Dark mode transitions */
      * {
        transition: background-color 0.2s ease-in-out, border-color 0.2s ease-in-out, color 0.2s ease-in-out;
      }
      
      /* Custom focus styles for dark mode */
      .dark-focus:focus {
        outline: none;
        box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.4);
      }
      
      .light-focus:focus {
        outline: none;
        box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.3);
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Helper function to check if a value is meaningful (not empty, zero, or '00000')
  const isValidValue = (value) => {
    if (!value) return false;
    if (value === null || value === undefined) return false;
    const stringValue = String(value).trim();
    if (stringValue === '' || stringValue === '0' || stringValue === '00000' || stringValue === 'null' || stringValue === 'undefined') return false;
    if (Number(value) === 0) return false;
    return true;
  };

  // Helper function to display value or fallback
  const displayValue = (value, fallback = 'N/A') => {
    return isValidValue(value) ? value : fallback;
  };

  // Series options
  const seriesOptions = [
    { value: 'RS', label: 'Regular Stock (RS)' },
    { value: 'AO', label: 'Against Order (AO)' },
    { value: 'SC', label: 'Stock Clearance (SC)' },
    { value: 'DC', label: 'Discontinued (DC)' },
    { value: 'ITF', label: 'Item to Focus (ITF)' },
    { value: 'OT', label: 'One Time (OT)' }
  ];

  // Limit options for react-select
  const limitOptions = [
    { value: 5, label: '5' },
    { value: 10, label: '10' },
    { value: 25, label: '25' },
    { value: 50, label: '50' },
    { value: 100, label: '100' }
  ];

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const total = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/items/count`, {
          params: { filter },
        });
        setTotalItems(total.data.count);
        if (total.data.count === 0 || total.data.count < (page - 1) * limit) {
          setPage(0);
        }
        const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/items`, {
          params: { page, limit, filter },
        });
        setItems(response.data);
        setFilteredItems(response.data);
      } catch (error) {
        Swal.fire('Error', 'Failed to fetch items :- ' + error.message, 'error');
      }
    };

    fetchItems();
  }, [page, limit, filter]);

  const totalPages = Math.ceil(totalItems / limit);

  // Fetch brands
  const fetchBrands = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/brands/all`);
      
      if (response.data.success && Array.isArray(response.data.brands)) {
        // Transform brands data for react-select and include additional fields
        const brandOptions = response.data.brands.map(brand => ({
          value: brand._id,
          label: brand.name,
          categories: brand.categories || [],
          additionalFields: brand.additionalFields || [],
          _id: brand._id  // Keep the full brand ID for reference
        }));
        setBrands(brandOptions);
        
        console.log('Fetched brands with additional fields:', brandOptions);
      } else {
        console.error('Invalid brands data:', response.data);
        setBrands([]);
      }
    } catch (error) {
      console.error('Error fetching brands:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to fetch brands'
      });
      setBrands([]);
    } finally {
      // Add a small delay to ensure react-select options are fully loaded
      setTimeout(() => {
        setLoading(false);
      }, 200);
    }
  };

  useEffect(() => {
    fetchBrands();
  }, []);

  // Handle brand selection
  const handleBrandChange = (selectedOption) => {
    console.log('Brand changed:', selectedOption);
    console.log('Available brands:', brands);
    
    setSelectedBrand(selectedOption);
    setSelectedCategory(null); // Reset category when brand changes
    
    // Find the brand and get its additional fields
    const brand = brands.find(b => b.value === selectedOption?.value);
    console.log('Found brand:', brand);
    console.log('Brand additional fields:', brand?.additionalFields);
    
    setBrandAdditionalFields(brand?.additionalFields || []);
    
    // Reset additional field values when brand changes
    const newAdditionalFieldValues = {};
    if (brand?.additionalFields && brand.additionalFields.length > 0) {
      brand.additionalFields.forEach(field => {
        newAdditionalFieldValues[field.fieldName] = field.defaultValue || '';
      });
    }
    
    setForm(prev => ({ 
      ...prev, 
      brand: selectedOption?.value, 
      category: '',
      additionalFieldValues: newAdditionalFieldValues
    }));
    console.log('Set additional fields:', brand?.additionalFields || []);
    console.log('New additional field values:', newAdditionalFieldValues);
  };

  // Handle category selection
  const handleCategoryChange = (selectedOption) => {
    setSelectedCategory(selectedOption);
    setForm(prev => ({ ...prev, category: selectedOption?.value }));
  };

  // Handle additional field value changes
  const handleAdditionalFieldValueChange = (fieldName, value) => {
    setForm(prev => ({
      ...prev,
      additionalFieldValues: {
        ...prev.additionalFieldValues,
        [fieldName]: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    console.log('🚀 Form submission started');
    console.log('📋 Current form state:', form);
    console.log('🔧 Additional field values:', form.additionalFieldValues);
    console.log('🏷️ Brand additional fields:', brandAdditionalFields);

    // Show progress modal
    const steps = isEditing 
      ? ['Preparing update', 'Processing image', 'Tracking changes', 'Updating item', 'Saving changes']
      : ['Preparing data', 'Processing image', 'Creating item', 'Saving item'];
      
    setProgressModal({
      isOpen: true,
      title: isEditing ? 'Updating Item' : 'Creating Item',
      steps,
      currentStep: 0,
      progress: 0,
      message: 'Initializing...'
    });

    try {
      const formData = new FormData();
      
      // Update progress
      setProgressModal(prev => ({
        ...prev,
        currentStep: 1,
        progress: 20,
        message: 'Preparing form data...'
      }));

      // Handle image conversion from draft data URL if needed
      let imageToSubmit = form.image;
      if (form.image && typeof form.image === 'string' && form.image.startsWith('data:')) {
        console.log('🔄 Converting draft image data URL to File for submission');
        setProgressModal(prev => ({
          ...prev,
          message: 'Converting draft image for upload...'
        }));
        
        imageToSubmit = await convertDataUrlToFile(
          form.image, 
          form._imageFileName || 'image.jpg', 
          form._imageFileSize
        );
      }

      Object.keys(form).forEach((key) => {
        if (key === 'image' && imageToSubmit) {
          formData.append(key, imageToSubmit);
        } else if (key === '_imageFileName' || key === '_imageFileSize') {
          // Skip metadata fields, don't send to server
        } else if (key === 'offerType') {
          const offerType = { ...form[key], flat: form.mrp };
          formData.append(key, JSON.stringify(offerType));
        } else if (key === 'additionalFieldValues') {
          console.log('📝 Adding additionalFieldValues to form:', form[key]);
          formData.append(key, JSON.stringify(form[key]));
        } else {
          formData.append(key, form[key]);
        }
      });

      if (!isEditing) {
        formData.append('createdBy', user.user._id);
      } else {
        formData.append('updatedBy', user.user._id);
      }

      // Update progress
      setProgressModal(prev => ({
        ...prev,
        currentStep: 2,
        progress: 40,
        message: form.image ? 'Processing image upload...' : 'Processing data...'
      }));

      let response;
      if (isEditing) {
        setProgressModal(prev => ({
          ...prev,
          currentStep: 3,
          progress: 60,
          message: 'Analyzing changes and updating item...'
        }));
        
        response = await axios.put(`${process.env.REACT_APP_API_BASE_URL}/items/${editingId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        setProgressModal(prev => ({
          ...prev,
          currentStep: 3,
          progress: 70,
          message: 'Creating new item...'
        }));
        
        response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/items`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      // Update progress
      setProgressModal(prev => ({
        ...prev,
        currentStep: steps.length - 1,
        progress: 90,
        message: 'Finalizing...'
      }));

      // Show success information from response
      if (response.data.success) {
        let successMessage = isEditing ? 'Item updated successfully' : 'Item created successfully';
        
        if (response.data.imageResult) {
          if (response.data.imageResult.isDuplicate) {
            successMessage += '\n\nImage: Found existing image and linked to item';
          } else {
            successMessage += '\n\nImage: New image processed and saved';
          }
        }
        
        if (response.data.changedFields && response.data.changedFields.length > 0) {
          successMessage += `\n\nChanges tracked: ${response.data.changedFields.join(', ')}`;
        }

        // Delete the draft after successful item creation/update
        if (draftManager.currentDraftId) {
          try {
            await draftManager.deleteDraft(draftManager.currentDraftId);
            console.log('✅ Draft deleted after successful item save');
          } catch (draftError) {
            console.error('⚠️ Failed to delete draft:', draftError);
            // Don't show error to user, draft deletion is not critical
          }
        }

        // Complete progress
        setProgressModal(prev => ({
          ...prev,
          progress: 100,
          message: 'Complete!'
        }));

        setTimeout(() => {
          setProgressModal(prev => ({ ...prev, isOpen: false }));
          Swal.fire({
            title: 'Success',
            text: successMessage,
            icon: 'success',
            confirmButtonText: 'OK'
          });
          resetForm();
        }, 1000);
      }

    } catch (error) {
      console.error('Submit error:', error);
      
      setProgressModal(prev => ({ ...prev, isOpen: false }));
      
      let errorMessage = 'Failed to save item';
      if (error.response?.data?.details) {
        errorMessage += `\n\nDetails: ${error.response.data.details}`;
      }
      
      Swal.fire('Error', errorMessage, 'error');
    }
  };

  const validateForm = () => {
    const requiredFields = [
      'brand',
      'category',
      'skuCode',
      'skuDescription',
      'series',
      'hsnCode',
      'gstRate',
      'mrp',
    ];

    if (requiredFields.some((field) => !form[field])) {
      Swal.fire('Warning', 'All fields are required', 'warning');
      return false;
    }

    if (!form.image) {
      Swal.fire('Warning', 'Image is required', 'warning');
      return false;
    }

    if (form.image && form.image.type && !form.image.type.startsWith('image/')) {
      Swal.fire('Warning', 'Image must be a valid image file', 'warning');
      return false;
    }
    return true;
  };

  const handleFilterChange = (e) => {
    const value = e.target.value;
    setFilter(value);
    const filtered = items.filter(
      (item) =>
        item.brand.name.toLowerCase().includes(value.toLowerCase()) ||
        item.skuCode.toLowerCase().includes(value.toLowerCase())
    );
    setFilteredItems(filtered);
  };

  const resetForm = () => {
    setForm({
      brand: '',
      category: '',
      skuCode: '',
      skuDescription: '',
      series: 'RS',
      hsnCode: '',
      gstRate: '',
      mrp: '',
      offerType: {
        nrp: '',
        clp: '',
        sdp: '',
        kap: '',
        hvr: '',
        spl: '',
      },
      image: null,
      additionalFieldValues: {}
    });
    setImagePreview(null);
    setSelectedBrand(null);
    setSelectedCategory(null);
    setBrandAdditionalFields([]);
    setIsEditing(false);
    setEditingId(null);
  };

  const handleEdit = async (item) => {
    // Delete any existing draft when editing an item
    if (draftManager.currentDraftId) {
      try {
        await draftManager.deleteDraft(draftManager.currentDraftId);
        console.log('✅ Draft deleted when editing item');
      } catch (draftError) {
        console.error('⚠️ Failed to delete draft on edit:', draftError);
        // Continue anyway, draft deletion is not critical
      }
    }

    const convertUrlToFile = async (url) => {
      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Failed to fetch image: ${response.status}`);
        }
        const data = await response.blob();
        return new File([data], `image.${data.type.split('/')[1]}`, { type: data.type });
      } catch (error) {
        console.error('Error converting URL to file:', error);
        // Return a placeholder or null if image fetch fails
        return null;
      }
    };
    
    setIsEditing(true);
    setEditingId(item._id);
    
    // Try to fetch the image
    const imageFile = await convertUrlToFile(
      `${process.env.REACT_APP_API_BASE_URL}/items/image/${item._id}`
    );

    // Validate image file if it was successfully fetched
    if (imageFile && imageFile.type && !imageFile.type.startsWith('image/')) {
      Swal.fire('Error', 'Only image files are allowed!', 'error');
      return;
    }

    // Set the selected brand and category for react-select
    const selectedBrandOption = brands.find(brand => brand.value === item.brand._id);
    setSelectedBrand(selectedBrandOption);
    
    // Load brand additional fields
    const brand = brands.find(b => b.value === item.brand._id);
    setBrandAdditionalFields(brand?.additionalFields || []);
    
    const selectedCategoryOption = selectedBrandOption?.categories.find(cat => cat._id === item.category._id);
    const categorySelectOption = selectedCategoryOption ? {
      value: selectedCategoryOption._id,
      label: selectedCategoryOption.name
    } : null;
    setSelectedCategory(categorySelectOption);

    setForm({
      brand: item.brand._id,
      category: item.category._id,
      skuCode: item.skuCode,
      skuDescription: item.skuDescription,
      series: item.series || 'RS',
      hsnCode: item.hsnCode,
      gstRate: item.gstRate,
      mrp: item.mrp,
      offerType: item.offerType,
      image: imageFile || null, // Handle null case if image fetch failed
      additionalFieldValues: item.additionalFieldValues || {}
    });
    
    // Set image preview only if we have a valid image
    if (imageFile) {
      setImagePreview(`${process.env.REACT_APP_API_BASE_URL}/items/image/${item._id}`);
    } else {
      setImagePreview(null);
      // Optionally show a warning
      console.warn('⚠️ Failed to load item image, you may need to re-upload');
    }
    
    // Focus on SKU code field after a short delay to ensure the form is rendered
    setTimeout(() => {
      const skuCodeInput = document.getElementById('skuCode');
      if (skuCodeInput) {
        skuCodeInput.focus();
        skuCodeInput.scrollIntoView({ behavior: 'smooth' });
        skuCodeInput.select();
      }
    }, 300); // Increased timeout from 100ms to 300ms for better react-select option loading
  };

  // Load latest item with populated audit trail and open the modal
  const showItemDetails = async (item) => {
    try {
      // Prefer stable _id based fetch to avoid SKU mismatches
      const { data } = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/items/id/${encodeURIComponent(item._id)}`);
      setModalItem(data);
      setModalOpen(true);
    } catch (err) {
      // Fallback: try by skuCode once
      try {
        const { data } = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/items/${encodeURIComponent(item.skuCode)}`);
        setModalItem(data);
      } catch (_) {
        setModalItem(item);
      }
      setModalOpen(true);
    }
  };

  // Show UpdatedBy history modal
  const showUpdateHistory = async (item) => {
    console.log('🔍 showUpdateHistory called with item:', {
      id: item._id,
      skuCode: item.skuCode,
      hasUpdatedBy: !!item.updatedBy,
      updatedByLength: item.updatedBy?.length
    });
    
    try {
      const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/items/id/${encodeURIComponent(item._id)}`;
      console.log('📡 Making API call to:', apiUrl);
      console.log('🌍 API Base URL:', process.env.REACT_APP_API_BASE_URL);
      
      const { data } = await axios.get(apiUrl);
      
      console.log('✅ API response received:', {
        id: data._id,
        skuCode: data.skuCode,
        hasUpdatedBy: !!data.updatedBy,
        updatedByLength: data.updatedBy?.length
      });
      
      setUpdatedByModal({
        isOpen: true,
        item: data
      });
    } catch (error) {
      console.error('❌ Error fetching item history:', error);
      console.error('❌ Error details:', {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      Swal.fire('Error', `Failed to load update history: ${error.message}`, 'error');
    }
  };

  // Custom styles for react-select with dark mode support
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: '42px',
      backgroundColor: darkMode ? '#374151' : '#ffffff',
      borderColor: darkMode ? '#4B5563' : (state.isFocused ? '#6366F1' : '#D1D5DB'),
      boxShadow: state.isFocused ? (darkMode ? '0 0 0 1px #6366F1' : '0 0 0 1px #6366F1') : 'none',
      '&:hover': {
        borderColor: darkMode ? '#6B7280' : '#9CA3AF'
      },
      color: darkMode ? '#F3F4F6' : '#111827'
    }),
    menu: (base) => ({
      ...base,
      backgroundColor: darkMode ? '#374151' : '#ffffff',
      border: darkMode ? '1px solid #4B5563' : '1px solid #D1D5DB'
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected 
        ? (darkMode ? '#4F46E5' : '#6366F1')
        : state.isFocused 
          ? (darkMode ? '#4B5563' : '#F3F4F6')
          : (darkMode ? '#374151' : '#ffffff'),
      color: state.isSelected ? '#ffffff' : (darkMode ? '#F3F4F6' : '#111827'),
      '&:hover': {
        backgroundColor: state.isSelected 
          ? (darkMode ? '#4F46E5' : '#6366F1')
          : (darkMode ? '#4B5563' : '#E5E7EB')
      }
    }),
    singleValue: (base) => ({
      ...base,
      color: darkMode ? '#F3F4F6' : '#111827'
    }),
    input: (base) => ({
      ...base,
      color: darkMode ? '#F3F4F6' : '#111827'
    }),
    placeholder: (base) => ({
      ...base,
      color: darkMode ? '#9CA3AF' : '#6B7280'
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: darkMode ? '#4B5563' : '#E5E7EB'
    }),
    multiValueLabel: (base) => ({
      ...base,
      color: darkMode ? '#F3F4F6' : '#111827'
    }),
    multiValueRemove: (base) => ({
      ...base,
      color: darkMode ? '#9CA3AF' : '#6B7280',
      '&:hover': {
        backgroundColor: darkMode ? '#EF4444' : '#FEE2E2',
        color: darkMode ? '#ffffff' : '#DC2626'
      }
    })
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
      {/* Enhanced Toast Notification with Dark Mode */}
      {showNotification && (
        <div 
          className={`fixed top-4 right-4 z-50 border-l-4 border-green-500 rounded-lg shadow-2xl p-4 flex items-center space-x-3 animate-fade-in transform transition-all duration-300 ${
            darkMode 
              ? 'bg-gray-800 border-green-400' 
              : 'bg-white border-green-500'
          }`}
          style={{ 
            minWidth: '320px',
            animation: 'slideInFromRight 0.4s ease-out'
          }}
        >
          <div className={`flex-shrink-0 p-1 rounded-full ${
            darkMode ? 'bg-green-900/30' : 'bg-green-50'
          }`}>
            <svg className={`h-5 w-5 ${
              darkMode ? 'text-green-400' : 'text-green-500'
            }`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="flex-1">
            <p className={`text-sm font-medium transition-colors duration-300 ${
              darkMode ? 'text-gray-100' : 'text-gray-900'
            }`}>
              {notificationMessage}
            </p>
          </div>
          <button
            onClick={() => setShowNotification(false)}
            className={`flex-shrink-0 p-1 rounded-full transition-all duration-200 hover:scale-110 ${
              darkMode 
                ? 'text-gray-400 hover:text-gray-200 hover:bg-gray-700' 
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
            }`}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      <div className={`p-6 shadow-2xl rounded-2xl transition-all duration-300 ${
        darkMode 
          ? 'bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900 border border-gray-700' 
          : 'bg-gradient-to-br from-white via-white to-gray-50 border border-gray-100'
      }`}>
        {/* Header */}
        <div className="mb-4 sm:mb-6 md:mb-8">
          <div className={`flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 sm:p-6 rounded-xl transition-colors duration-300 ${
            darkMode 
              ? 'bg-gradient-to-r from-indigo-900 via-purple-900 to-pink-900' 
              : 'bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600'
          }`}>
            <div className="flex items-center space-x-3 sm:space-x-4 mb-3 sm:mb-0">
              <div className="bg-white bg-opacity-20 backdrop-blur-sm p-2 sm:p-3 rounded-lg">
                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white drop-shadow-lg">
                  Item Management
                </h2>
                <p className="text-indigo-100 text-xs sm:text-sm mt-1">Manage your product inventory</p>
              </div>
            </div>
            <div className="flex items-center space-x-2 bg-white bg-opacity-20 backdrop-blur-sm px-3 sm:px-4 py-2 rounded-lg">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span className="text-white font-medium text-sm sm:text-base">{isEditing ? 'Edit Mode' : 'Create Mode'}</span>
            </div>
          </div>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
        {/* Form Fields Card */}
        <div className={`p-4 sm:p-6 rounded-xl border transition-all duration-300 ${
          darkMode 
            ? 'bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900 border-gray-600 shadow-2xl' 
            : 'bg-gradient-to-br from-white via-white to-gray-50 border-gray-200 shadow-xl'
        }`}>
          <div className={`mb-4 sm:mb-6 pb-4 border-b ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
            <h3 className={`text-lg sm:text-xl font-bold flex items-center transition-colors duration-300 ${
              darkMode ? 'text-white' : 'text-gray-900'
            }`}>
              <div className={`p-2 rounded-lg mr-3 ${darkMode ? 'bg-indigo-600' : 'bg-indigo-100'}`}>
                <svg className={`w-4 h-4 sm:w-5 sm:h-5 ${darkMode ? 'text-white' : 'text-indigo-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              Product Information
            </h3>
            <p className={`text-xs sm:text-sm mt-2 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
              Enter the basic details for your product item
            </p>
          </div>

          {/* All form fields in a responsive grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Brand */}
            <div className="space-y-2">
              <label className={`block font-semibold text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                Brand <span className="text-red-500">*</span>
              </label>
              <Select
                value={selectedBrand}
                onChange={handleBrandChange}
                options={brands}
                isLoading={loading}
                isClearable
                isSearchable
                placeholder="Select a brand..."
                className="basic-single"
                id='brand'
                classNamePrefix="select"
                noOptionsMessage={() => "No brands found"}
                loadingMessage={() => "Loading brands..."}
                styles={customSelectStyles}
              />
              {!loading && brands.length === 0 && (
                <p className="text-sm text-red-500">
                  No brands available. Please add brands first.
                </p>
              )}
            </div>

            {/* Category */}
            <div className="space-y-2">
              <label className={`block font-semibold text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                Category <span className="text-red-500">*</span>
              </label>
              <Select
                value={selectedCategory}
                onChange={handleCategoryChange}
                options={selectedBrand?.categories?.map(category => ({
                  value: category._id,
                  label: category.name
                })) || []}
                isDisabled={!selectedBrand}
                isClearable
                isSearchable
                placeholder={selectedBrand ? "Select a category..." : "Select a brand first"}
                className="basic-single"
                classNamePrefix="select"
                noOptionsMessage={() => selectedBrand ? "No categories found" : "Select a brand first"}
                styles={customSelectStyles}
              />
            </div>
          
            {/* SKU Code */}
            <div className="space-y-2">
              <label className={`block font-semibold text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                SKU Code <span className="text-red-500">*</span>
              </label>
              <input
                id="skuCode"
                type="text"
                value={form.skuCode}
                onChange={(e) => setForm({ ...form, skuCode: e.target.value })}
                className={`w-full px-3 py-2.5 border rounded-lg transition-all duration-200 focus:ring-2 focus:ring-indigo-500 focus:border-transparent hover:border-indigo-400 ${
                  darkMode 
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:bg-gray-600 focus:shadow-lg' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:bg-gray-50 focus:shadow-lg'
                }`}
                placeholder="Enter SKU Code"
              />
            </div>

            {/* SKU Description */}
            <div className="space-y-2 sm:col-span-2 lg:col-span-1">
              <label className={`block font-semibold text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                SKU Description <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.skuDescription}
                onChange={(e) => setForm({ ...form, skuDescription: e.target.value })}
                className={`w-full px-3 py-2.5 border rounded-lg transition-all duration-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                  darkMode 
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                }`}
                placeholder="Enter SKU Description"
              />
            </div>

            {/* Series */}
            <div className="space-y-2">
              <label className={`block font-semibold text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                Series <span className="text-red-500">*</span>
              </label>
              <Select
                value={seriesOptions.find(option => option.value === form.series)}
                onChange={(selectedOption) => setForm({ ...form, series: selectedOption?.value || 'RS' })}
                options={seriesOptions}
                isSearchable={false}
                placeholder="Select Series..."
                className="basic-single"
                classNamePrefix="select"
                styles={customSelectStyles}
              />
            </div>

            {/* HSN Code */}
            <div className="space-y-2">
              <label className={`block font-semibold text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                HSN Code <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={form.hsnCode}
                onChange={(e) => setForm({ ...form, hsnCode: e.target.value })}
                className={`w-full px-3 py-2.5 border rounded-lg transition-all duration-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                  darkMode 
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                }`}
                placeholder="Enter HSN Code"
              />
            </div>

            {/* GST Rate */}
            <div className="space-y-2">
              <label className={`block font-semibold text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                GST Rate (%) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={form.gstRate}
                onChange={(e) => setForm({ ...form, gstRate: e.target.value })}
                className={`w-full px-3 py-2.5 border rounded-lg transition-all duration-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                  darkMode 
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                }`}
                placeholder="e.g., 18"
              />
            </div>

            {/* Product Image Upload */}
            <div className="space-y-2 sm:col-span-2 lg:col-span-3">
              <label className={`block font-semibold text-sm transition-colors duration-300 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                Product Image
              </label>

              <div className="flex items-center justify-center w-full">
                <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer transition-all duration-300 ${
                  darkMode
                    ? 'border-indigo-600 bg-indigo-900/10 hover:bg-indigo-900/20 hover:border-indigo-500'
                    : 'border-indigo-300 bg-indigo-50 hover:bg-indigo-100 hover:border-indigo-400'
                }`}>
                  <div className="flex flex-col items-center justify-center pt-5 pb-6">
                    <svg className={`w-6 h-6 sm:w-8 sm:h-8 mb-4 ${darkMode ? 'text-indigo-400' : 'text-indigo-500'}`} aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 16">
                      <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 13h3a3 3 0 0 0 0-6h-.025A5.56 5.56 0 0 0 16 6.5 5.5 5.5 0 0 0 5.207 5.021C5.137 5.017 5.071 5 5 5a4 4 0 0 0 0 8h2.167M10 15V6m0 0L8 8m2-2 2 2"/>
                    </svg>
                    <p className={`mb-2 text-xs sm:text-sm ${darkMode ? 'text-indigo-300' : 'text-indigo-600'}`}>
                      <span className="font-semibold">Click to upload</span> or drag and drop
                    </p>
                    <p className={`text-xs ${darkMode ? 'text-indigo-400' : 'text-indigo-500'}`}>
                      JPG, PNG, GIF, WebP (MAX. 5MB)
                    </p>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files[0];
                      if (file) {
                        if (file.type && !file.type.startsWith('image/')) {
                          Swal.fire('Error', 'Please select a valid image file', 'error');
                          e.target.value = '';
                          return;
                        }
                        if (file.size > 5 * 1024 * 1024) {
                          Swal.fire('Error', 'Image size should be less than 5MB', 'error');
                          e.target.value = '';
                          return;
                        }
                        setForm({ ...form, image: file });
                        setImagePreview(URL.createObjectURL(file));
                        console.log('🖼️ Image selected for draft:', file.name, file.size);
                      }
                    }}
                  />
                </label>
              </div>
              {imagePreview && (
                <div className="relative mt-4">
                  <div className="relative">
                    <img 
                      src={imagePreview} 
                      alt="Item Preview" 
                      className={`w-full max-w-xs h-32 sm:h-48 object-cover rounded-lg border-2 transition-colors duration-300 ${
                        darkMode ? 'border-gray-600' : 'border-gray-300'
                      }`}
                    />
                    {/* Draft indicator for images loaded from draft */}
                    {typeof form.image === 'string' && form._imageFileName && (
                      <div className={`absolute bottom-0 left-0 right-0 px-2 py-1 rounded-b-lg text-xs ${
                        darkMode 
                          ? 'bg-indigo-900/80 text-indigo-300 backdrop-blur-sm' 
                          : 'bg-blue-900/80 text-blue-100 backdrop-blur-sm'
                      }`}>
                        📄 From Draft: {form._imageFileName}
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setImagePreview(null);
                        setForm({ ...form, image: null, _imageFileName: null, _imageFileSize: null });
                        const fileInput = document.querySelector('input[type="file"]');
                        if (fileInput) fileInput.value = '';
                      }}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center text-xs sm:text-sm hover:bg-red-600 transition-colors duration-200 shadow-lg"
                      title="Remove image"
                    >
                      <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Pricing Section */}
          <div className="mt-4 sm:mt-6">
            <div className={`p-4 rounded-lg border transition-colors duration-300 ${
              darkMode
                ? 'bg-gradient-to-r from-emerald-900/30 to-teal-900/30 border-emerald-700/50'
                : 'bg-gradient-to-r from-emerald-50/50 to-teal-50/50 border-emerald-200/50'
            }`}>
              <h4 className={`text-sm sm:text-base font-semibold mb-4 flex items-center transition-colors duration-300 ${
                darkMode ? 'text-emerald-300' : 'text-emerald-700'
              }`}>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
                Pricing Information
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {/* MRP - Required field */}
                <div className="space-y-2">
                  <label className={`block text-sm font-medium transition-colors duration-300 ${
                    darkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    MRP (₹) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.mrp}
                    onChange={(e) => setForm({ ...form, mrp: e.target.value })}
                    className={`w-full px-3 py-2 border rounded-lg transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 hover:border-emerald-400 ${
                      darkMode
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:bg-gray-600'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:bg-gray-50'
                    }`}
                    placeholder="Enter MRP"
                    required
                  />
                </div>

                {/* NRP */}
                <div className="space-y-2">
                  <label className={`block text-sm font-medium transition-colors duration-300 ${
                    darkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    NRP (₹)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.offerType.nrp}
                    onChange={(e) =>
                      setForm({ ...form, offerType: { ...form.offerType, nrp: e.target.value } })
                    }
                    className={`w-full px-3 py-2 border rounded-lg transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 hover:border-emerald-400 ${
                      darkMode
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:bg-gray-600'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:bg-gray-50'
                    }`}
                    placeholder="Net Rate Price"
                  />
                </div>

                {/* CLP */}
                <div className="space-y-2">
                  <label className={`block text-sm font-medium transition-colors duration-300 ${
                    darkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    CLP (₹)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.offerType.clp}
                    onChange={(e) =>
                      setForm({ ...form, offerType: { ...form.offerType, clp: e.target.value } })
                    }
                    className={`w-full px-3 py-2 border rounded-lg transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 hover:border-emerald-400 ${
                      darkMode
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:bg-gray-600'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:bg-gray-50'
                    }`}
                    placeholder="Customer Landing Price"
                  />
                </div>

                {/* SDP */}
                <div className="space-y-2">
                  <label className={`block text-sm font-medium transition-colors duration-300 ${
                    darkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    SDP (₹)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.offerType.sdp}
                    onChange={(e) =>
                      setForm({ ...form, offerType: { ...form.offerType, sdp: e.target.value } })
                    }
                    className={`w-full px-3 py-2 border rounded-lg transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 hover:border-emerald-400 ${
                      darkMode
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:bg-gray-600'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:bg-gray-50'
                    }`}
                    placeholder="Special Dealer Price"
                  />
                </div>

                {/* KAP */}
                <div className="space-y-2">
                  <label className={`block text-sm font-medium transition-colors duration-300 ${
                    darkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    KAP (₹)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.offerType.kap}
                    onChange={(e) =>
                      setForm({ ...form, offerType: { ...form.offerType, kap: e.target.value } })
                    }
                    className={`w-full px-3 py-2 border rounded-lg transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 hover:border-emerald-400 ${
                      darkMode
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:bg-gray-600'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:bg-gray-50'
                    }`}
                    placeholder="Key Account Price"
                  />
                </div>

                {/* HVR */}
                <div className="space-y-2">
                  <label className={`block text-sm font-medium transition-colors duration-300 ${
                    darkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    HVR (₹)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.offerType.hvr}
                    onChange={(e) =>
                      setForm({ ...form, offerType: { ...form.offerType, hvr: e.target.value } })
                    }
                    className={`w-full px-3 py-2 border rounded-lg transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 hover:border-emerald-400 ${
                      darkMode
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:bg-gray-600'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:bg-gray-50'
                    }`}
                    placeholder="High Volume Rate"
                  />
                </div>

                {/* SPL */}
                <div className="space-y-2">
                  <label className={`block text-sm font-medium transition-colors duration-300 ${
                    darkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    SPL (₹)
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.offerType.spl}
                    onChange={(e) =>
                      setForm({ ...form, offerType: { ...form.offerType, spl: e.target.value } })
                    }
                    className={`w-full px-3 py-2 border rounded-lg transition-all duration-200 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 hover:border-emerald-400 ${
                      darkMode
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:bg-gray-600'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:bg-gray-50'
                    }`}
                    placeholder="Special Price"
                  />
                </div>
              </div>
            </div>
          </div>

          </div>

          {/* Additional Fields Section */}
          {brandAdditionalFields.length > 0 && (
            <div className="mt-4 sm:mt-6 col-span-full">
              <div className={`p-4 rounded-lg border transition-colors duration-300 ${
                darkMode
                  ? 'bg-gradient-to-r from-purple-900/30 to-pink-900/30 border-purple-700/50'
                  : 'bg-gradient-to-r from-purple-50/50 to-pink-50/50 border-purple-200/50'
              }`}>
                <h4 className={`text-sm sm:text-base font-semibold mb-4 flex items-center transition-colors duration-300 ${
                  darkMode ? 'text-purple-300' : 'text-purple-700'
                }`}>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17v4a2 2 0 002 2h4M13 13h3a2 2 0 012 2v1" />
                  </svg>
                  Additional Brand Fields
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {brandAdditionalFields.map((field, index) => (
                    <div key={index} className="space-y-2">
                      <label className={`block text-sm font-medium transition-colors duration-300 ${
                        darkMode ? 'text-gray-200' : 'text-gray-700'
                      }`}>
                        {field.fieldName}
                        {field.isRequired && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      {field.fieldType === 'number' ? (
                        <input
                          type="number"
                          value={form.additionalFieldValues[field.fieldName] || ''}
                          onChange={(e) => handleAdditionalFieldValueChange(field.fieldName, e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg transition-all duration-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                            darkMode
                              ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                          }`}
                          placeholder={`Enter ${field.fieldName}`}
                          required={field.isRequired}
                        />
                      ) : (
                        <input
                          type="text"
                          value={form.additionalFieldValues[field.fieldName] || ''}
                          onChange={(e) => handleAdditionalFieldValueChange(field.fieldName, e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg transition-all duration-200 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                            darkMode
                              ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                              : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                          }`}
                          placeholder={`Enter ${field.fieldName}`}
                          required={field.isRequired}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Enhanced Action Buttons */}
          <div className={`mt-4 sm:mt-6 p-4 rounded-lg border-t flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 transition-all duration-300 col-span-full ${
            darkMode 
              ? 'bg-gradient-to-r from-gray-700/40 to-gray-800/40 border-gray-600 backdrop-blur-sm' 
              : 'bg-gradient-to-r from-gray-50/80 to-gray-100/80 border-gray-200 backdrop-blur-sm'
          }`}>
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <button 
                type="submit" 
                className={`group relative bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg font-semibold transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:scale-[1.02] flex items-center justify-center space-x-2 ${
                  darkMode ? 'hover:shadow-indigo-500/25' : 'hover:shadow-indigo-500/20'
                }`}
              >
                {isEditing ? (
                  <>
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Update Item</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                    </svg>
                    <span>Create Item</span>
                  </>
                )}
                <div className="absolute inset-0 bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg blur opacity-0 group-hover:opacity-30 transition-opacity duration-300 -z-10"></div>
              </button>
              {isEditing && (
                <button
                  type="button"
                  onClick={resetForm}
                  className={`px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-[1.02] flex items-center justify-center space-x-2 ${
                    darkMode
                      ? 'bg-gray-600 hover:bg-gray-500 text-gray-100'
                      : 'bg-gray-200 hover:bg-gray-300 text-gray-800'
                  }`}
                >
                  <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span>Cancel</span>
                </button>
              )}
            </div>
            {isEditing && (
              <div className={`text-xs sm:text-sm px-3 sm:px-4 py-2 rounded-lg flex items-center justify-center sm:justify-start space-x-2 ${
                darkMode ? 'bg-amber-900/30 text-amber-300 border border-amber-700/50' : 'bg-amber-50 text-amber-700 border border-amber-200'
              }`}>
                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                <span>Editing Mode</span>
              </div>
            )}
          </div>
        </form>
      </div>

      {/* Items list section */}
      <div className="mt-6 sm:mt-8">
      <div className={`p-4 sm:p-6 shadow-2xl rounded-xl border transition-all duration-300 ${
        darkMode 
          ? 'bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900 border-gray-700' 
          : 'bg-gradient-to-br from-white via-white to-gray-50 border-gray-200'
      }`}>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6 gap-3">
          <h2 className={`text-xl sm:text-2xl lg:text-3xl font-bold transition-colors duration-300 ${
            darkMode ? 'text-white' : 'text-gray-800'
          }`}>
            Items
          </h2>
          <div className={`text-xs sm:text-sm px-2 sm:px-3 py-1 rounded-full transition-colors duration-300 ${
            darkMode 
              ? 'bg-gray-700 text-gray-300' 
              : 'bg-gray-100 text-gray-500'
          }`}>
            Total: {totalItems} items
          </div>
        </div>
        
        {/* Enhanced search bar and entries selector */}
        <div className="flex flex-col sm:flex-row lg:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 space-y-3 sm:space-y-0 sm:space-x-4 lg:space-x-6">
          {/* Search Bar */}
          <div className="w-full sm:w-2/3 lg:w-2/3 relative">
            <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
              <svg className={`h-4 w-4 sm:h-5 sm:w-5 transition-colors duration-300 ${darkMode ? 'text-gray-400' : 'text-gray-400'}`} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
              </svg>
            </div>
            <input
              type="text"
              value={filter}
              onChange={handleFilterChange}
              className={`w-full pl-10 sm:pl-12 pr-3 sm:pr-4 py-2.5 sm:py-3 border rounded-lg shadow-sm transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm sm:text-base hover:shadow-md ${
                darkMode
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400 focus:bg-gray-600 focus:shadow-lg focus:shadow-indigo-500/10'
                  : 'bg-white border-gray-300 text-gray-700 placeholder-gray-500 focus:bg-gray-50 focus:shadow-lg focus:shadow-indigo-500/10'
              }`}
              placeholder="Search by brand name or SKU code..."
            />
            {filter && (
              <button
                onClick={() => {
                  setFilter('');
                  setFilteredItems(items);
                }}
                className={`absolute inset-y-0 right-0 pr-3 sm:pr-4 flex items-center transition-colors duration-200 ${
                  darkMode 
                    ? 'text-gray-400 hover:text-gray-200' 
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                <svg className="h-4 w-4 sm:h-5 sm:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
          
          {/* Entries Selector */}
          <div className={`flex items-center space-x-2 sm:space-x-3 px-3 sm:px-4 py-2.5 sm:py-3 rounded-lg border shadow-sm transition-colors duration-300 ${
            darkMode
              ? 'bg-gradient-to-r from-gray-700 to-gray-800 border-gray-600'
              : 'bg-gradient-to-r from-gray-50 to-gray-100 border-gray-200'
          }`}>
            <span className={`text-xs sm:text-sm font-medium whitespace-nowrap transition-colors duration-300 ${
              darkMode ? 'text-gray-200' : 'text-gray-700'
            }`}>
              Show
            </span>
            <div className="w-16 sm:w-20">
              <Select
                value={limitOptions.find(option => option.value === limit)}
                onChange={(selectedOption) => setLimit(selectedOption?.value || 5)}
                options={limitOptions}
                isSearchable={false}
                className="basic-single"
                classNamePrefix="select"
                styles={{
                  control: (base) => ({
                    ...base,
                    minHeight: '28px',
                    border: 'none',
                    boxShadow: 'none',
                    backgroundColor: darkMode ? '#374151' : 'white',
                    fontSize: '0.875rem',
                    '&:hover': {
                      border: 'none'
                    }
                  }),
                  singleValue: (base) => ({
                    ...base,
                    color: darkMode ? '#F3F4F6' : '#111827',
                    fontSize: '0.875rem'
                  }),
                  menu: (base) => ({
                    ...base,
                    backgroundColor: darkMode ? '#374151' : '#ffffff',
                    border: darkMode ? '1px solid #4B5563' : '1px solid #D1D5DB',
                    fontSize: '0.875rem'
                  }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isSelected 
                      ? (darkMode ? '#4F46E5' : '#6366F1')
                      : state.isFocused 
                        ? (darkMode ? '#4B5563' : '#F3F4F6')
                        : (darkMode ? '#374151' : '#ffffff'),
                    color: state.isSelected ? '#ffffff' : (darkMode ? '#F3F4F6' : '#111827'),
                    fontSize: '0.875rem'
                  }),
                  valueContainer: (base) => ({
                    ...base,
                    padding: '0 6px'
                  }),
                  indicatorSeparator: () => ({
                    display: 'none'
                  }),
                  dropdownIndicator: (base) => ({
                    ...base,
                    padding: '2px',
                    color: darkMode ? '#9CA3AF' : '#6B7280'
                  })
                }}
              />
            </div>
            <span className={`text-xs sm:text-sm font-medium whitespace-nowrap transition-colors duration-300 ${
              darkMode ? 'text-gray-200' : 'text-gray-700'
            }`}>
              entries
            </span>
          </div>
        </div>

        {/* Enhanced TABLE for sm+ screens */}
        <div className={`hidden sm:block overflow-hidden rounded-lg border shadow-sm transition-colors duration-300 ${
          darkMode ? 'border-gray-700' : 'border-gray-200'
        }`}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className={`transition-all duration-300 ${
              darkMode 
                ? 'bg-gradient-to-r from-gray-700 via-gray-700 to-gray-800 border-b border-gray-600' 
                : 'bg-gradient-to-r from-gray-50 via-gray-50 to-gray-100 border-b border-gray-200'
            }`}>
              <tr>
                <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider transition-colors duration-300 ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Brand
                </th>
                <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider transition-colors duration-300 ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  SKU Code
                </th>
                <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider transition-colors duration-300 ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Description
                </th>
                <th className={`px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider transition-colors duration-300 ${
                  darkMode ? 'text-gray-200' : 'text-gray-700'
                }`}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className={`divide-y transition-colors duration-300 ${
              darkMode 
                ? 'bg-gray-800 divide-gray-700' 
                : 'bg-white divide-gray-200'
            }`}>
              {filteredItems?.map((item, index) => (
                <tr key={item._id} className={`transition-all duration-200 border-b ${
                  darkMode
                    ? `hover:bg-gray-700/80 hover:shadow-lg border-gray-700/50 ${index % 2 === 0 ? 'bg-gray-800' : 'bg-gray-800/70'}`
                    : `hover:bg-gray-50/80 hover:shadow-md border-gray-100 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-25'}`
                }`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`text-sm font-medium transition-colors duration-300 ${
                      darkMode ? 'text-gray-100' : 'text-gray-900'
                    }`}>
                      {item.brand?.name}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`text-sm font-mono px-2 py-1 rounded transition-colors duration-300 ${
                      darkMode 
                        ? 'text-gray-100 bg-gray-700' 
                        : 'text-gray-900 bg-gray-100'
                    }`}>
                      {item.skuCode}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className={`text-sm max-w-xs truncate transition-colors duration-300 ${
                      darkMode ? 'text-gray-200' : 'text-gray-900'
                    }`} title={item.skuDescription}>
                      {item.skuDescription}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-150"
                        onClick={() => handleEdit(item)}
                      >
                        Edit
                      </button>
                      <button
                        className={`inline-flex items-center px-3 py-1.5 border text-xs font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-150 ${
                          darkMode
                            ? 'border-gray-600 text-gray-200 bg-gray-700 hover:bg-gray-600'
                            : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                        }`}
                        onClick={() => showItemDetails(item)}
                      >
                        Details
                      </button>
                      {item.updatedBy && item.updatedBy.length > 0 && (
                        <button
                          className="inline-flex items-center px-3 py-1.5 border border-amber-300 text-xs font-medium rounded-md text-amber-700 bg-amber-50 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-colors duration-150"
                          onClick={() => showUpdateHistory(item)}
                          title={`View ${item.updatedBy.length} updates`}
                        >
                          <ClockIcon className="w-3 h-3 mr-1" />
                          History ({item.updatedBy.length})
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Enhanced GRID CARDS for mobile devices */}
        <div className="sm:hidden grid grid-cols-1 gap-4">
          {filteredItems?.map((item) => (
            <div key={item._id} className={`shadow-lg rounded-xl p-4 border transition-all duration-300 transform hover:scale-[1.02] ${
              darkMode
                ? 'bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700 hover:shadow-2xl hover:shadow-gray-900/20 hover:border-gray-600'
                : 'bg-gradient-to-br from-white to-gray-50 border-gray-200 hover:shadow-2xl hover:shadow-gray-900/10 hover:border-gray-300'
            }`}>
              <div className="flex justify-between items-start mb-3">
                <h3 className={`text-lg font-bold truncate transition-colors duration-300 ${
                  darkMode ? 'text-white' : 'text-gray-800'
                }`}>
                  {item.skuDescription}
                </h3>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                  item.series === 'RS' ? (darkMode ? 'bg-green-900/30 text-green-300 border border-green-700' : 'bg-green-100 text-green-800 border border-green-200') :
                  item.series === 'AO' ? (darkMode ? 'bg-blue-900/30 text-blue-300 border border-blue-700' : 'bg-blue-100 text-blue-800 border border-blue-200') :
                  item.series === 'SC' ? (darkMode ? 'bg-yellow-900/30 text-yellow-300 border border-yellow-700' : 'bg-yellow-100 text-yellow-800 border border-yellow-200') :
                  item.series === 'DC' ? (darkMode ? 'bg-red-900/30 text-red-300 border border-red-700' : 'bg-red-100 text-red-800 border border-red-200') :
                  item.series === 'ITF' ? (darkMode ? 'bg-purple-900/30 text-purple-300 border border-purple-700' : 'bg-purple-100 text-purple-800 border border-purple-200') :
                  (darkMode ? 'bg-gray-700 text-gray-300 border border-gray-600' : 'bg-gray-100 text-gray-800 border border-gray-200')
                }`}>
                  {seriesOptions.find(opt => opt.value === item.series)?.label || item.series}
                </span>
              </div>
              <div className="space-y-2 mb-4">
                <div className="flex justify-between">
                  <span className={`text-sm font-medium transition-colors duration-300 ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    Brand:
                  </span>
                  <span className={`text-sm transition-colors duration-300 ${
                    darkMode ? 'text-gray-200' : 'text-gray-900'
                  }`}>
                    {item.brand?.name}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className={`text-sm font-medium transition-colors duration-300 ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    SKU Code:
                  </span>
                  <span className={`text-sm font-mono px-2 py-0.5 rounded transition-colors duration-300 ${
                    darkMode 
                      ? 'text-gray-100 bg-gray-700' 
                      : 'text-gray-900 bg-gray-100'
                  }`}>
                    {item.skuCode}
                  </span>
                </div>
              </div>
              <div className="flex space-x-2">
                <button
                  className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-150"
                  onClick={() => handleEdit(item)}
                >
                  Edit
                </button>
                <button
                  className={`flex-1 inline-flex justify-center items-center px-3 py-2 border text-sm font-medium rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-150 ${
                    darkMode
                      ? 'border-gray-600 text-gray-200 bg-gray-700 hover:bg-gray-600'
                      : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
                  }`}
                  onClick={() => showItemDetails(item)}
                >
                  Details
                </button>
                {item.updatedBy && item.updatedBy.length > 0 && (
                  <button
                    className="flex-1 inline-flex justify-center items-center px-3 py-2 border border-amber-300 text-sm font-medium rounded-md text-amber-700 bg-amber-50 hover:bg-amber-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500 transition-colors duration-150"
                    onClick={() => showUpdateHistory(item)}
                    title={`View ${item.updatedBy.length} updates`}
                  >
                    <ClockIcon className="w-4 h-4 mr-1" />
                    History
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Enhanced Pagination controls */}
        <div className="flex flex-col sm:flex-row justify-between items-center mt-6 sm:mt-8 space-y-3 sm:space-y-0">
          {/* Pagination info */}
          <div className={`text-xs sm:text-sm transition-colors duration-300 order-2 sm:order-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Showing <span className={`font-semibold transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{page * limit + 1}</span> to{' '}
            <span className={`font-semibold transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{Math.min((page + 1) * limit, totalItems)}</span> of{' '}
            <span className={`font-semibold transition-colors duration-300 ${darkMode ? 'text-white' : 'text-gray-900'}`}>{totalItems}</span> entries
          </div>
          
          {/* Pagination buttons */}
          <div className="flex items-center space-x-0.5 sm:space-x-1 order-1 sm:order-2">
            <button
              onClick={() => setPage(page - 1)}
              disabled={page === 0}
              className={`relative inline-flex items-center px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium border rounded-l-md focus:z-10 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 ${
                darkMode
                  ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600 hover:shadow-md disabled:bg-gray-800'
                  : 'text-gray-500 bg-white border-gray-300 hover:bg-gray-50 hover:shadow-md disabled:bg-gray-100'
              }`}
            >
              <svg className="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
              <span className="hidden sm:inline">Previous</span>
              <span className="sm:hidden">Prev</span>
            </button>
            
            {/* Page numbers */}
            {[page - 1, page, page + 1].map((index) =>
              index >= 0 && index < totalPages ? (
                <button
                  key={index}
                  onClick={() => setPage(index)}
                  className={`relative inline-flex items-center px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium border transition-colors duration-150 ${
                    page === index
                      ? 'z-10 bg-indigo-600 border-indigo-600 text-white'
                      : darkMode
                        ? 'bg-gray-700 border-gray-600 text-gray-300 hover:bg-gray-600 focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500'
                  }`}
                >
                  {index + 1}
                </button>
              ) : null
            )}
            
            <button
              onClick={() => setPage(page + 1)}
              disabled={page === totalPages - 1}
              className={`relative inline-flex items-center px-2 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium border rounded-r-md focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-150 ${
                darkMode
                  ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600'
                  : 'text-gray-500 bg-white border-gray-300 hover:bg-gray-50'
              }`}
            >
              <span className="hidden sm:inline">Next</span>
              <span className="sm:hidden">Next</span>
              <svg className="w-3 h-3 sm:w-4 sm:h-4 ml-0.5 sm:ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Stylish Modal for Item Details */}
      {modalOpen && modalItem && (
  <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
    {/* Enhanced background overlay with blur effect */}
    <div
      className={`absolute inset-0 backdrop-blur-md transition-all duration-300 ${
        darkMode 
          ? 'bg-black/70' 
          : 'bg-black/50'
      }`}
      onClick={() => setModalOpen(false)}
    />

    {/* Modal container - optimized for tablet screens with reduced height */}
    <div className={`relative w-full max-w-4xl max-h-[75vh] rounded-2xl shadow-2xl overflow-hidden animate-pulse-scale flex flex-col border ${
      darkMode 
        ? 'bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900 border-gray-700 shadow-black/50' 
        : 'bg-gradient-to-br from-white via-white to-gray-50 border-gray-200 shadow-gray-900/20'
    }`}>
      
      {/* Header with gradient background */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 text-white px-6 py-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold">Item Details</h3>
            <p className="text-purple-100 text-xs mt-1">{modalItem.skuCode}</p>
          </div>
          <button
            className="text-white hover:text-gray-200 transition-colors duration-200 p-2 rounded-full hover:bg-white hover:bg-opacity-20"
            onClick={() => setModalOpen(false)}
            aria-label="Close modal"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Modal content with better spacing */}
      <div className={`flex-1 p-6 overflow-auto ${
        darkMode ? 'scrollbar-dark' : 'scrollbar-light'
      }`}
           style={{ maxHeight: 'calc(75vh - 140px)' }}>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* Image section - takes 1 column */}
          <div className="lg:col-span-1">
            <div className={`rounded-xl p-5 shadow-lg border ${
              darkMode 
                ? 'bg-gradient-to-br from-gray-700 to-gray-600 border-gray-600' 
                : 'bg-gradient-to-br from-gray-50 to-gray-100 border-gray-200'
            }`}>
              <h4 className={`text-base font-semibold mb-4 text-center ${
                darkMode ? 'text-gray-200' : 'text-gray-800'
              }`}>Product Image</h4>
              <div className={`aspect-square rounded-xl shadow-lg overflow-hidden ring-2 ${
                darkMode ? 'bg-gray-900 ring-gray-600' : 'bg-white ring-gray-200'
              }`}>
                <img
                  src={`${process.env.REACT_APP_API_BASE_URL}/items/image/${modalItem._id}`}
                  alt={modalItem.skuDescription}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                />
              </div>
            </div>
          </div>

          {/* Details section - takes 2 columns */}
          <div className="lg:col-span-2 space-y-5">
            
            {/* Basic Information */}
            <div className={`rounded-xl p-4 border ${
              darkMode
                ? 'bg-gradient-to-r from-indigo-900/50 to-purple-900/50 border-indigo-700'
                : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200'
            }`}>
              <h4 className={`text-base font-bold mb-3 flex items-center ${
                darkMode ? 'text-gray-200' : 'text-gray-800'
              }`}>
                <svg className={`w-4 h-4 mr-2 ${darkMode ? 'text-indigo-400' : 'text-blue-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Basic Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className={`rounded-lg p-3 shadow-sm border ${
                  darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                }`}>
                  <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Brand</p>
                  <p className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{displayValue(modalItem.brand?.name)}</p>
                </div>
                <div className={`rounded-lg p-3 shadow-sm border ${
                  darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                }`}>
                  <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Category</p>
                  <p className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{displayValue(modalItem.category?.name)}</p>
                </div>
                <div className={`rounded-lg p-3 shadow-sm border md:col-span-2 ${
                  darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                }`}>
                  <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Description</p>
                  <p className={`text-sm ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>{displayValue(modalItem.skuDescription)}</p>
                </div>
              </div>
            </div>

            {/* Technical Details */}
            <div className={`rounded-xl p-4 border ${
              darkMode
                ? 'bg-gradient-to-r from-green-900/50 to-emerald-900/50 border-green-700'
                : 'bg-gradient-to-r from-green-50 to-emerald-50 border-green-200'
            }`}>
              <h4 className={`text-base font-bold mb-3 flex items-center ${
                darkMode ? 'text-gray-200' : 'text-gray-800'
              }`}>
                <svg className={`w-4 h-4 mr-2 ${darkMode ? 'text-green-400' : 'text-green-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Technical Details
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className={`rounded-lg p-3 shadow-sm border ${
                  darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                }`}>
                  <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>SKU Code</p>
                  <p className={`text-xs font-mono px-2 py-1 rounded-full ${
                    darkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-100 text-gray-800'
                  }`}>{displayValue(modalItem.skuCode)}</p>
                </div>
                <div className={`rounded-lg p-3 shadow-sm border ${
                  darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                }`}>
                  <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Series</p>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    modalItem.series === 'RS' ? (darkMode ? 'bg-green-900 text-green-200 border border-green-700' : 'bg-green-100 text-green-800 border border-green-300') :
                    modalItem.series === 'AO' ? (darkMode ? 'bg-blue-900 text-blue-200 border border-blue-700' : 'bg-blue-100 text-blue-800 border border-blue-300') :
                    modalItem.series === 'SC' ? (darkMode ? 'bg-yellow-900 text-yellow-200 border border-yellow-700' : 'bg-yellow-100 text-yellow-800 border border-yellow-300') :
                    modalItem.series === 'DC' ? (darkMode ? 'bg-red-900 text-red-200 border border-red-700' : 'bg-red-100 text-red-800 border border-red-300') :
                    modalItem.series === 'ITF' ? (darkMode ? 'bg-purple-900 text-purple-200 border border-purple-700' : 'bg-purple-100 text-purple-800 border border-purple-300') :
                    (darkMode ? 'bg-gray-700 text-gray-200 border border-gray-600' : 'bg-gray-100 text-gray-800 border border-gray-300')
                  }`}>
                    {displayValue(modalItem.series)}
                  </span>
                </div>
                <div className={`rounded-lg p-3 shadow-sm border ${
                  darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                }`}>
                  <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>HSN Code</p>
                  <p className={`text-xs font-mono px-2 py-1 rounded-full ${
                    darkMode ? 'bg-gray-600 text-gray-200' : 'bg-gray-100 text-gray-800'
                  }`}>{displayValue(modalItem.hsnCode)}</p>
                </div>
                <div className={`rounded-lg p-3 shadow-sm border ${
                  darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                }`}>
                  <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>GST Rate</p>
                  <p className={`text-sm font-bold ${darkMode ? 'text-orange-400' : 'text-orange-600'}`}>
                    {isValidValue(modalItem.gstRate) ? `${modalItem.gstRate}%` : 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Pricing Information */}
            <div className={`rounded-xl p-4 border ${
              darkMode
                ? 'bg-gradient-to-r from-purple-900/50 to-pink-900/50 border-purple-700'
                : 'bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200'
            }`}>
              <h4 className={`text-base font-bold mb-3 flex items-center ${
                darkMode ? 'text-gray-200' : 'text-gray-800'
              }`}>
                <svg className={`w-4 h-4 mr-2 ${darkMode ? 'text-purple-400' : 'text-purple-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
                Pricing Details
              </h4>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {/* MRP - always show */}
                <div className={`rounded-lg p-3 shadow-sm border-2 ${
                  darkMode ? 'bg-gray-700 border-green-700' : 'bg-white border-green-200'
                }`}>
                  <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>MRP</p>
                  <p className="text-sm font-bold text-green-600">
                    ₹{displayValue(modalItem.mrp)}
                  </p>
                </div>
                
                {/* Only show offer types that have meaningful values */}
                {isValidValue(modalItem.offerType?.nrp) && (
                  <div className={`rounded-lg p-3 shadow-sm border ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                  }`}>
                    <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>NRP</p>
                    <p className={`text-sm font-semibold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>₹{modalItem.offerType.nrp}</p>
                  </div>
                )}
                
                {isValidValue(modalItem.offerType?.clp) && (
                  <div className={`rounded-lg p-3 shadow-sm border ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                  }`}>
                    <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>CLP</p>
                    <p className={`text-sm font-semibold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>₹{modalItem.offerType.clp}</p>
                  </div>
                )}
                
                {isValidValue(modalItem.offerType?.sdp) && (
                  <div className={`rounded-lg p-3 shadow-sm border ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                  }`}>
                    <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>SDP</p>
                    <p className={`text-sm font-semibold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>₹{modalItem.offerType.sdp}</p>
                  </div>
                )}
                
                {isValidValue(modalItem.offerType?.kap) && (
                  <div className={`rounded-lg p-3 shadow-sm border ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                  }`}>
                    <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>KAP</p>
                    <p className={`text-sm font-semibold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>₹{modalItem.offerType.kap}</p>
                  </div>
                )}
                
                {isValidValue(modalItem.offerType?.hvr) && (
                  <div className={`rounded-lg p-3 shadow-sm border ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                  }`}>
                    <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>HVR</p>
                    <p className={`text-sm font-semibold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>₹{modalItem.offerType.hvr}</p>
                  </div>
                )}
                
                {isValidValue(modalItem.offerType?.spl) && (
                  <div className={`rounded-lg p-3 shadow-sm border ${
                    darkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'
                  }`}>
                    <p className={`text-xs font-medium mb-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>SPL</p>
                    <p className={`text-sm font-semibold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>₹{modalItem.offerType.spl}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Audit Information */}
            {(modalItem.createdBy || modalItem.updatedBy || modalItem.createdAt || modalItem.lastUpdatedAt) && (
              <div className={`rounded-xl p-4 border ${
                darkMode
                  ? 'bg-gradient-to-r from-gray-700 to-gray-600 border-gray-600'
                  : 'bg-gradient-to-r from-gray-50 to-slate-50 border-gray-200'
              }`}>
                <h4 className={`text-base font-bold mb-3 flex items-center ${
                  darkMode ? 'text-gray-200' : 'text-gray-800'
                }`}>
                  <svg className={`w-4 h-4 mr-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Audit Trail
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mb-4">
                  <div className={`p-3 rounded-lg border ${
                    darkMode ? 'bg-gray-600 border-gray-500 text-gray-200' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <p className={`font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Created At</p>
                    <p className={darkMode ? 'text-gray-200' : ''}>{modalItem.createdAt ? new Date(modalItem.createdAt).toLocaleString() : 'N/A'}</p>
                  </div>
                  <div className={`p-3 rounded-lg border ${
                    darkMode ? 'bg-gray-600 border-gray-500 text-gray-200' : 'bg-gray-50 border-gray-200'
                  }`}>
                    <p className={`font-semibold ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Last Updated At</p>
                    <p className={darkMode ? 'text-gray-200' : ''}>{modalItem.lastUpdatedAt ? new Date(modalItem.lastUpdatedAt).toLocaleString() : 'N/A'}</p>
                  </div>
                </div>

                {modalItem.updatedBy && modalItem.updatedBy.length > 0 && (
                  <div>
                    <h4 className={`text-md font-semibold mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>Update History</h4>
                    <div className="max-h-60 overflow-y-auto space-y-2 pr-2">
                      {modalItem.updatedBy.slice().reverse().map(log => {
                        const updaterName = (log.employee ? log.employee.name : '') || (log.employee ? log.employee.email : '') || (typeof log.employee === 'string' ? log.employee : 'Unknown');
                        
                        const keyChanges = {};
                        if (log.pastData) {
                          if (log.pastData.mrp !== undefined) keyChanges.MRP = log.pastData.mrp;
                          if (log.pastData.gstRate !== undefined) keyChanges['GST %'] = log.pastData.gstRate;
                          if (log.pastData.offerType) {
                            const { nrp, clp, sdp, kap, hvr, spl } = log.pastData.offerType;
                            if (nrp !== undefined) keyChanges.NRP = nrp;
                            if (clp !== undefined) keyChanges.CLP = clp;
                            if (sdp !== undefined) keyChanges.SDP = sdp;
                            if (kap !== undefined) keyChanges.KAP = kap;
                            if (hvr !== undefined) keyChanges.HVR = hvr;
                            if (spl !== undefined) keyChanges.SPL = spl;
                          }
                        }

                        return (
                          <div key={log._id} className={`p-3 rounded-lg border text-xs ${
                            darkMode ? 'bg-gray-600 border-gray-500' : 'bg-gray-50 border-gray-200'
                          }`}>
                            <div className="flex justify-between items-start">
                              <div>
                                <p className={`font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{updaterName}</p>
                                <p className={darkMode ? 'text-gray-400' : 'text-gray-500'}>{new Date(log.date).toLocaleString()}</p>
                              </div>
                              {log.pastData && Object.keys(log.pastData).length > 0 && (
                                <button 
                                  onClick={() => toggleUpdateDetails(log._id)}
                                  className={`text-sm font-medium ${
                                    darkMode 
                                      ? 'text-blue-400 hover:text-blue-300' 
                                      : 'text-blue-600 hover:text-blue-800'
                                  }`}
                                >
                                  {expandedUpdates[log._id] ? 'Hide Data' : 'View Data'}
                                </button>
                              )}
                            </div>

                            {Object.keys(keyChanges).length > 0 && (
                              <div className={`mt-2 pt-2 border-t ${darkMode ? 'border-gray-500' : 'border-gray-200'}`}>
                                <p className={`font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Key Previous Values:</p>
                                <div className={`grid grid-cols-2 sm:grid-cols-3 gap-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                                  {Object.entries(keyChanges).map(([key, value]) => (
                                    <div key={key}>
                                      <span className="font-semibold">{key}:</span> {value}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {expandedUpdates[log._id] && log.pastData && (
                              <div className={`mt-2 pt-2 border-t ${darkMode ? 'border-gray-500' : 'border-gray-200'}`}>
                                <p className={`font-medium mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>Full Previous Data:</p>
                                <pre className={`p-2 rounded border text-xs whitespace-pre-wrap break-all ${
                                  darkMode 
                                    ? 'bg-gray-700 border-gray-600 text-gray-300' 
                                    : 'bg-white border-gray-200 text-gray-700'
                                }`}>
                                  {JSON.stringify(
                                    (({ image, updatedBy, __v, ...rest }) => rest)(log.pastData), 
                                    null, 
                                    2
                                  )}
                                </pre>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
            {/* End Audit Section */}
          </div>{/* end details column */}
        </div>{/* end grid */}
      </div>{/* end content scroll area */}

      {/* Enhanced footer - now fixed at bottom */}
      <div className={`flex-shrink-0 px-6 py-4 border-t ${
        darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
      }`}>
        <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
          <div className={`text-xs order-2 sm:order-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Item ID: {modalItem._id}
          </div>
          <div className="flex space-x-3 order-1 sm:order-2">
            <button
              onClick={() => {
                handleEdit(modalItem);
                setModalOpen(false);
              }}
              className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center space-x-2 shadow-sm"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              <span>Edit Item</span>
            </button>
            <button
              onClick={() => setModalOpen(false)}
              className="bg-gray-600 hover:bg-gray-700 text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 shadow-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
)}

      {/* Progress Modal */}
      <ProgressModal
        isOpen={progressModal.isOpen}
        onClose={() => setProgressModal(prev => ({ ...prev, isOpen: false }))}
        title={progressModal.title}
        steps={progressModal.steps}
        currentStep={progressModal.currentStep}
        progress={progressModal.progress}
        message={progressModal.message}
      />

      {/* UpdatedBy History Modal */}
      <UpdatedByModal
        isOpen={updatedByModal.isOpen}
        onClose={() => setUpdatedByModal({ isOpen: false, item: null })}
        item={updatedByModal.item}
        darkMode={darkMode}
      />

      </div>
    </div>
  );
};

export default ItemManagement;
